import type { App } from '@xb/shared';
import type { ReactNode } from 'react';
import { Navigate, NavLink, Outlet, useOutletContext, useParams } from 'react-router';
import { api } from '../api.js';
import { ErrorNotice, Loading } from '../components/Notice.js';
import { StatusPill, TypeBadge } from '../components/StatusPill.js';
import { useInterval, useResource } from '../hooks.js';

export interface AppContext {
  app: App;
  reload: () => void;
  setApp: (app: App) => void;
}

export function useApp(): AppContext {
  return useOutletContext<AppContext>();
}

/** Frame for one app: header, section tabs, and the app record shared with each tab. */
export function AppPage() {
  const { slug = '' } = useParams();
  const res = useResource(() => api.getApp(slug), [slug]);
  const app = res.data?.app;

  // Provisioning and deleting are asynchronous on the platform side; keep the status fresh.
  const busy = app?.status === 'provisioning' || app?.status === 'deleting';
  useInterval(res.reload, busy ? 5000 : null);

  if (res.error) return <ErrorNotice error={res.error} />;
  if (!app) return <Loading what="Loading app" />;

  const ctx: AppContext = { app, reload: res.reload, setApp: (a) => res.set({ app: a }) };
  const base = `/apps/${encodeURIComponent(app.slug)}`;
  const tabs: { to: string; label: string; end?: boolean; serverOnly?: boolean }[] = [
    { to: base, label: 'Overview', end: true },
    { to: `${base}/build`, label: 'Build' },
    { to: `${base}/deploy`, label: 'Deploy' },
    { to: `${base}/people`, label: 'People' },
    { to: `${base}/environment`, label: 'Environment', serverOnly: true },
    { to: `${base}/activity`, label: 'Activity' },
    { to: `${base}/logs`, label: 'Logs', serverOnly: true },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{app.name}</h1>
          <div className="meta btn-row" style={{ marginTop: '0.35rem' }}>
            <span className="mono small subtle">{app.slug}</span>
            <TypeBadge type={app.type} />
            <StatusPill status={app.status} />
          </div>
        </div>
        {app.status === 'active' ? (
          <a className="btn" href={`https://${app.host}`} target="_blank" rel="noreferrer">
            Open app ↗
          </a>
        ) : null}
      </div>
      <nav className="tabs" aria-label="App sections">
        {tabs
          .filter((t) => !t.serverOnly || app.type === 'server')
          .map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end}>
              {t.label}
            </NavLink>
          ))}
      </nav>
      <Outlet context={ctx} />
    </>
  );
}

/** Wrap a server-only tab: static apps get sent back to the overview. */
export function ServerOnly({ children }: { children: ReactNode }) {
  const { app } = useApp();
  if (app.type !== 'server') return <Navigate to={`/apps/${encodeURIComponent(app.slug)}`} replace />;
  return <>{children}</>;
}
